import type { EnrichedLead } from "@/lib/data";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScoreGauge, ScoreBadge } from "@/components/ScoreGauge";
import {
	Building2,
	MapPin,
	Users,
	DollarSign,
	TrendingUp,
	Globe,
	ChevronRight,
	Zap,
} from "lucide-react";

function getSignalIcon(type: string) {
	switch (type) {
		case "website_visit": return <Globe className="w-3 h-3" />;
		case "content_download": return <TrendingUp className="w-3 h-3" />;
		case "job_posting": return <Users className="w-3 h-3" />;
		case "tech_adoption": return <Zap className="w-3 h-3" />;
		case "funding_event": return <DollarSign className="w-3 h-3" />;
		case "expansion": return <Building2 className="w-3 h-3" />;
		default: return <Zap className="w-3 h-3" />;
	}
}

function getSignalColor(strength: string) {
	switch (strength) {
		case "high": return "bg-red-50 text-red-700 border-red-200";
		case "medium": return "bg-amber-50 text-amber-700 border-amber-200";
		case "low": return "bg-slate-50 text-slate-600 border-slate-200";
		default: return "bg-slate-50 text-slate-600 border-slate-200";
	}
}

function timeAgo(timestamp: string): string {
	const diff = Date.now() - new Date(timestamp).getTime();
	const days = Math.floor(diff / (1000 * 60 * 60 * 24));
	if (days === 0) return "Today";
	if (days === 1) return "Yesterday";
	if (days < 7) return `${days}d ago`;
	return `${Math.floor(days / 7)}w ago`;
}

export function LeadCard({
	lead,
	onClick,
}: {
	lead: EnrichedLead;
	onClick: () => void;
}) {
	const { company, contacts, signals, score } = lead;
	const topSignals = signals.sort((a, b) => {
		const order = { high: 0, medium: 1, low: 2 };
		return order[a.strength] - order[b.strength];
	}).slice(0, 2);

	return (
		<Card
			className="group cursor-pointer transition-all duration-200 hover:shadow-md hover:border-blue-200 bg-white border-slate-200"
			onClick={onClick}
		>
			<CardContent className="p-5">
				<div className="flex items-start gap-4">
					{/* Company logo */}
					<div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm shadow-sm">
						{company.logo}
					</div>

					{/* Main content */}
					<div className="flex-1 min-w-0">
						<div className="flex items-start justify-between gap-3">
							<div>
								<div className="flex items-center gap-2">
									<h3 className="font-semibold text-foreground group-hover:text-blue-700 transition-colors truncate">
										{company.name}
									</h3>
									<ScoreBadge score={score.overall} />
								</div>
								<p className="text-sm text-muted-foreground mt-0.5 truncate">
									{company.domain}
								</p>
							</div>
							<div className="flex-shrink-0 flex items-center gap-3">
								<ScoreGauge score={score} size="sm" />
								<ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
							</div>
						</div>

						{/* Meta row */}
						<div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground">
							<span className="flex items-center gap-1">
								<Building2 className="w-3 h-3" />
								{company.industry}
							</span>
							<span className="flex items-center gap-1">
								<MapPin className="w-3 h-3" />
								{company.location}
							</span>
							<span className="flex items-center gap-1">
								<Users className="w-3 h-3" />
								{company.size}
							</span>
							<span className="flex items-center gap-1">
								<DollarSign className="w-3 h-3" />
								{company.revenue}
							</span>
						</div>

						{/* Signals and contacts row */}
						<div className="flex items-center justify-between mt-3">
							<div className="flex flex-wrap items-center gap-1.5">
								{topSignals.map((signal) => (
									<span
										key={signal.id}
										className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border ${getSignalColor(signal.strength)}`}
									>
										{getSignalIcon(signal.type)}
										{signal.title.length > 30 ? signal.title.slice(0, 30) + "..." : signal.title}
									</span>
								))}
								{signals.length > 2 && (
									<span className="text-[11px] text-muted-foreground">
										+{signals.length - 2} more
									</span>
								)}
							</div>
							<div className="flex items-center -space-x-2">
								{contacts.slice(0, 3).map((contact) => (
									<div
										key={contact.id}
										className="w-7 h-7 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px] font-medium text-slate-600"
										title={`${contact.name} - ${contact.title}`}
									>
										{contact.avatar}
									</div>
								))}
								{contacts.length > 3 && (
									<div className="w-7 h-7 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-[10px] font-medium text-slate-500">
										+{contacts.length - 3}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
